// Invoices register (BUILD_SPEC.md §11, §8.4 step 5). Plan.md G23.
import { useState } from 'react';
import { Button, Group, Select, Stack, Table } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconReceiptOff } from '@tabler/icons-react';
import { INVOICE_STATUSES, type InvoiceStatus } from '@zebra/core';
import { formatDate, formatMoneyCents } from './format';
import { useInvoicesList } from './api';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { CustomerName } from './components/EntityNames';

export function InvoicesRegister() {
  const [status, setStatus] = useState<InvoiceStatus | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useInvoicesList({ cursor, status });

  return (
    <Stack gap="md">
      <PageHeader
        title="Invoices"
        description="Every invoice raised from a dispatch, its status, and whether it's synced to Xero — the step after dispatch in sales order → dispatch → invoice → credit note."
        actions={
          <Select
            placeholder="All statuses"
            clearable
            data={INVOICE_STATUSES.map((s) => ({ value: s, label: s }))}
            value={status ?? null}
            onChange={(v) => {
              setStatus((v as InvoiceStatus) ?? undefined);
              setCursors([undefined]);
            }}
            w={160}
          />
        }
      />

      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Issue date</Table.Th>
              <Table.Th>Due date</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.items ?? []).map((invoice) => (
              <Table.Tr key={invoice.id}>
                <Table.Td>
                  <Link to="/sales-invoicing/invoices/$invoiceId" params={{ invoiceId: invoice.id }}>
                    {invoice.number}
                  </Link>
                </Table.Td>
                <Table.Td>
                  <CustomerName id={invoice.customerId} />
                </Table.Td>
                <Table.Td>{formatDate(invoice.issueDate)}</Table.Td>
                <Table.Td>{formatDate(invoice.dueDate)}</Table.Td>
                <Table.Td>
                  <DocStatusBadge status={invoice.status} domain="invoice" />
                </Table.Td>
                <Table.Td ta="right">{formatMoneyCents(invoice.totalCents)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <EmptyState
                    icon={IconReceiptOff}
                    title="No invoices yet"
                    hint="Invoices are created from posted dispatches — dispatch a load, then raise its invoice."
                    action={
                      <Button component={Link} to="/sales-invoicing/raise" variant="light">
                        Go to Raise invoices
                      </Button>
                    }
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
